// src/pages/admin/CompanyUserManagement.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyService, userService } from '../../services/apiServices';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/admin/StatusBadge';
import RoleBadge from '../../components/admin/RoleBadge';
import SearchFilterBar from '../../components/admin/SearchFilterBar';
import DataTable from '../../components/admin/DataTable';
import ConfirmationDialog from '../../components/admin/ConfirmationDialog';
import Pagination from '../../components/admin/Pagination';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast, showWarningToast } from '../../utils/toastUtils';
import MfaActionButton from '../../components/admin/MfaActionButton';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import { validatePassword } from '../../utils/passwordValidator';

const CompanyUserManagement = () => {
  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, refreshUser } = useAuth();

  // State for users and company
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'simpleuser',
    status: 'active',
    mfaRequired: false
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch users on component mount or when filters change
  useEffect(() => {
    if (companyId) {
      fetchUsers();
    }
  }, [companyId, pagination.page, pagination.limit, searchTerm, roleFilter, statusFilter]);

  // Fetch company details on mount
  useEffect(() => {
    if (companyId) {
      fetchCompany();
    }
  }, [companyId]);

  // Function to fetch company details
  const fetchCompany = async () => {
    try {
      const companyResponse = await companyService.getCompanyById(companyId);
      if (companyResponse.success) {
        setCompany(companyResponse.data);
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };

  // Function to fetch company users
  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare filters for API
      const filters = {
        companyId: companyId,
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchTerm) filters.search = searchTerm;
      if (roleFilter !== 'all') filters.role = roleFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;

      const response = await userService.getUsers(filters);

      if (response.success) {
        setUsers(response.data || []);
        // Update pagination from response
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.pagination
          }));
        } else {
          // Fallback
          setPagination(prev => ({
            ...prev,
            total: response.data?.length || 0,
            pages: Math.ceil((response.data?.length || 0) / prev.limit)
          }));
        }
      } else {
        throw new Error(response.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Erreur lors du chargement des utilisateurs. Veuillez réessayer plus tard.');
      showErrorToast('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort users
  // Note: Filtering is now largely handled on the server side
  const filteredUsers = [...users]
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'lastLogin' || sortConfig.key === 'createdAt') {
        if (!aValue && !bValue) return 0;
        if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

  // Calculate metrics
  const totalUsersMetric = pagination.total || users.length;
  const activeUsersCount = users.filter(user => user.status === 'active').length;
  const adminCount = users.filter(user => user.role === 'admin').length;
  const simpleUsersCount = users.filter(user => user.role === 'simpleuser').length;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      showWarningToast('Le nom est requis');
      return;
    }
    if (!formData.email.trim()) {
      showWarningToast('L\'email est requis');
      return;
    }
    if (!isEditing && !formData.password.trim()) {
      showWarningToast('Le mot de passe est requis');
      return;
    }
    if ((!isEditing || formData.password.trim()) && !validatePassword(formData.password).isValid) {
      showWarningToast('Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole');
      return;
    }

    const action = isEditing ? 'Mise à jour' : 'Création';
    const loadingToastId = showLoadingToast(`${action} de l'utilisateur en cours...`);

    setIsSubmitting(true);
    setError(null);

    try {
      const userData = {
        ...formData,
        companyId: companyId // Ensure user is assigned to this company
      };

      if (isEditing) {
        // For updates, handle password separately if provided
        const hasPasswordUpdate = userData.password && userData.password.trim();

        // Remove password from main user data - it must go through /password endpoint
        delete userData.password;

        // Update existing user
        const response = await userService.updateUser(currentUserId, userData);

        if (response.success) {
          // If password was provided, update it separately
          if (hasPasswordUpdate) {
            try {
              await userService.updatePassword(currentUserId, {
                newPassword: formData.password
              });
            } catch (passwordError) {
              console.error('Password update error:', passwordError);
              updateToast(loadingToastId, 'Utilisateur mis à jour, mais erreur lors du changement de mot de passe', 'warning');
              resetForm();
              return;
            }
          }

          setUsers(
            users.map((user) =>
              user._id === currentUserId
                ? { ...user, ...response.data }
                : user
            )
          );

          updateToast(loadingToastId, 'Utilisateur mis à jour avec succès', 'success');
          resetForm();
        } else {
          throw new Error(response.message || 'Failed to update user');
        }
      } else {
        // Create new user
        const response = await userService.createUser(userData);

        if (response.success) {
          setUsers([...users, response.data]);
          updateToast(loadingToastId, 'Utilisateur créé avec succès', 'success');
          resetForm();

          // Refresh current user data to update limits if current user is admin
          if (refreshUser && currentUser.role === 'admin') {
            await refreshUser();
          }
        } else {
          throw new Error(response.message || 'Failed to create user');
        }
      }
    } catch (error) {
      console.error(isEditing ? 'Update user error:' : 'Create user error:', error);
      const errorMessage = `Erreur lors de ${isEditing ? 'la mise à jour' : 'la création'} de l'utilisateur. ${error.message || 'Veuillez réessayer.'}`;
      setError(errorMessage);
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    if (!user || typeof user !== 'object') {
      console.error('Invalid user object:', user);
      return;
    }

    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'simpleuser',
      status: user.status || 'active',
      mfaRequired: user.mfa?.requiredByAdmin || false
    });

    setCurrentUserId(user._id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleStatusToggle = async (user) => {
    const action = user.status === 'active' ? 'désactiver' : 'activer';
    const loadingToastId = showLoadingToast(`${action.charAt(0).toUpperCase() + action.slice(1)} l'utilisateur...`);

    setIsSubmitting(true);

    try {
      const updatedStatus = user.status === 'active' ? 'inactive' : 'active';

      const response = await userService.updateUser(user._id, { status: updatedStatus });

      if (response.success) {
        setUsers(
          users.map((u) =>
            u._id === user._id
              ? { ...u, status: updatedStatus }
              : u
          )
        );
        updateToast(loadingToastId, `Utilisateur ${updatedStatus === 'active' ? 'activé' : 'désactivé'} avec succès`, 'success');
      } else {
        throw new Error(response.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Update user status error:', error);
      const errorMessage = 'Erreur lors de la mise à jour du statut. Veuillez réessayer.';
      setError(errorMessage);
      updateToast(loadingToastId, errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsSubmitting(true);
    setShowConfirmation(false);

    try {
      const response = await userService.deleteUser(userToDelete._id);

      if (response.success) {
        setUsers(users.filter(u => u._id !== userToDelete._id));
        setUserToDelete(null);
        showSuccessToast('Utilisateur supprimé avec succès');

        // Refresh current user data to update limits if current user is admin
        if (refreshUser && currentUser.role === 'admin') {
          await refreshUser();
        }
      } else {
        throw new Error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      const errorMessage = 'Erreur lors de la suppression de l\'utilisateur. Veuillez réessayer.';
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'simpleuser',
      status: 'active'
    });
    setIsEditing(false);
    setCurrentUserId(null);
    setShowForm(false);
    setError(null);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    switch (key) {
      case 'role':
        setRoleFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      default:
        break;
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  // Table columns definition
  const columns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value) => <span className="font-medium text-gray-900 text-sm">{value || '-'}</span>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || '-'}</span>
    },
    {
      key: 'role',
      label: 'Rôle',
      sortable: true,
      render: (value) => <RoleBadge role={value} size="sm" />
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value) => <StatusBadge status={value === 'active' ? 'Actif' : 'Inactif'} type={value === 'active' ? 'success' : 'danger'} size="sm" />
    },
    {
      key: 'cguAccepted',
      label: 'CGU',
      sortable: true,
      render: (value) => (
        <div className="flex items-center justify-center text-sm font-bold" title={value ? 'Conditions acceptées' : 'Conditions non acceptées'}>
          {value ? (
            <span className="text-green-700 bg-green-100 border border-green-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">✅</span> Oui
            </span>
          ) : (
            <span className="text-gray-600 bg-gray-100 border border-gray-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">❌</span> Non
            </span>
          )}
        </div>
      )
    },
    {
      key: 'lastLogin',
      label: 'Dernière connexion',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value ? new Date(value).toLocaleString() : 'Jamais'}</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, user) => (
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => handleEdit(user)}
            className="text-xs text-indigo-600 hover:text-indigo-900 font-medium px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
            disabled={isSubmitting}
          >
            Modifier
          </button>

          <button
            onClick={() => handleStatusToggle(user)}
            className={
              user.status === 'active'
                ? 'text-xs text-red-600 hover:text-red-900 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors'
                : 'text-xs text-green-600 hover:text-green-900 font-medium px-2 py-1 rounded hover:bg-green-50 transition-colors'
            }
            disabled={isSubmitting}
          >
            {user.status === 'active' ? 'Désactiver' : 'Activer'}
          </button>

          <MfaActionButton user={user} onUpdate={fetchUsers} compact />

          <button
            onClick={() => handleDeleteClick(user)}
            className="text-xs text-red-600 hover:text-red-900 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
            disabled={isSubmitting}
          >
            Supprimer
          </button>
        </div>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <button
            onClick={() => navigate('/superadmin/companies')}
            className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour aux entreprises
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Utilisateurs - {company?.name || 'Entreprise'}
          </h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          disabled={isSubmitting}
        >
          {showForm ? 'Annuler' : 'Nouvel Utilisateur'}
        </button>
      </div>

      {/* Company Info */}
      {company && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Nom de l'entreprise</p>
              <p className="text-lg font-semibold text-gray-900">{company.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="text-gray-700">{company.email || 'Non spécifié'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Statut</p>
              <StatusBadge
                status={company.status === 'active' ? 'Actif' : 'Inactif'}
                type={company.status === 'active' ? 'success' : 'danger'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total</p>
              <p className="text-2xl font-semibold text-gray-900">{totalUsersMetric}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Actifs</p>
              <p className="text-2xl font-semibold text-gray-900">{activeUsersCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Admins</p>
              <p className="text-2xl font-semibold text-gray-900">{adminCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Utilisateurs</p>
              <p className="text-2xl font-semibold text-gray-900">{simpleUsersCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
          <button
            onClick={fetchUsers}
            className="ml-2 text-red-800 hover:text-red-900 font-medium underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* User Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {isEditing ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isEditing ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!isEditing}
                  disabled={isSubmitting}
                />
                {(!isEditing || formData.password) && (
                  <PasswordRequirements password={formData.password} />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitting}
                >
                  <option value="admin">Administrateur</option>
                  <option value="simpleuser">Utilisateur Simple</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitting}
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer py-2.5 px-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-all duration-200 w-full bg-white/50 hover:bg-white">
                  <input
                    type="checkbox"
                    name="mfaRequired"
                    checked={formData.mfaRequired}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-700">MFA obligatoire</span>
                    <p className="text-xs text-gray-400 mt-0.5">Configurer à la prochaine connexion</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'En cours...' : (isEditing ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search and Filter Bar */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={[
          {
            key: 'role',
            label: 'Rôle',
            value: roleFilter,
            options: [
              { value: 'all', label: 'Tous les rôles' },
              { value: 'admin', label: 'Administrateur' },
              { value: 'simpleuser', label: 'Utilisateur Simple' }
            ]
          },
          {
            key: 'status',
            label: 'Statut',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous les statuts' },
              { value: 'active', label: 'Actif' },
              { value: 'inactive', label: 'Inactif' }
            ]
          }
        ]}
        onFilterChange={handleFilterChange}
        onReset={resetFilters}
      />

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <DataTable
          data={filteredUsers}
          columns={columns}
          sortConfig={sortConfig}
          onSort={handleSort}
          emptyMessage="Aucun utilisateur trouvé pour cette entreprise"
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />

      {/* Results summary */}
      {filteredUsers.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-sm text-gray-600">
          Affichage de {filteredUsers.length} utilisateur(s) sur {pagination.total} au total
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default CompanyUserManagement;